// src/Accordion/components/composed/AccordionContent.tsx
import React from "react";
import * as RadixAccordion from "@radix-ui/react-accordion";
import { Primitive } from "@radix-ui/react-primitive";
import { cva } from "class-variance-authority";

// src/lib/utils/merge-props.ts
var mergeProps = ({ className, ref, ...rest }, props) => ({
  ...rest,
  ...props,
  className,
  ref
});

// src/Accordion/constants.ts
var AccordionTestIds = {
  Accordion: "kui-accordion",
  AccordionContent: "kui-accordion-content",
  AccordionHeader: "kui-accordion-header",
  AccordionItem: "kui-accordion-item",
  AccordionRoot: "kui-accordion-root",
  AccordionTrigger: "kui-accordion-trigger",
  AccordionLabel: "kui-accordion-label",
  AccordionIcon: "kui-accordion-icon"
};

// src/Accordion/components/composed/AccordionContent.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/accordion.min.css";
import { jsx } from "react/jsx-runtime";
var accordionContent = cva("kui-accordion-content");
var AccordionContent = React.forwardRef(({ children, className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(RadixAccordion.Content, { asChild: true, children: /* @__PURE__ */ jsx(
    Primitive.div,
    {
      ...mergeProps(
        {
          className: accordionContent({ className }),
          "data-testid": AccordionTestIds.AccordionContent,
          ref
        },
        props
      ),
      children
    }
  ) });
});
AccordionContent.displayName = "AccordionContent";

// src/Accordion/components/composed/AccordionItem.tsx
import React2 from "react";
import * as RadixAccordion2 from "@radix-ui/react-accordion";
import { cva as cva2 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/accordion.min.css";
import { jsx as jsx2 } from "react/jsx-runtime";
var accordionItem = cva2("kui-accordion-item");
var AccordionItem2 = React2.forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx2(
    RadixAccordion2.AccordionItem,
    {
      ...mergeProps(
        {
          className: accordionItem({ className }),
          "data-testid": AccordionTestIds.AccordionItem,
          ref
        },
        props
      ),
      children
    }
  );
});
AccordionItem2.displayName = "AccordionItem";

// src/Accordion/components/composed/AccordionRoot.tsx
import React3 from "react";
import * as RadixAccordion3 from "@radix-ui/react-accordion";
import { cva as cva3 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/accordion.min.css";
import { jsx as jsx3 } from "react/jsx-runtime";
var accordionRoot = cva3("kui-accordion-root");
var AccordionRoot = React3.forwardRef(({ children, className, kind, ...props }, ref) => {
  return /* @__PURE__ */ jsx3(
    RadixAccordion3.Root,
    {
      type: kind,
      ...mergeProps(
        {
          className: accordionRoot({ className }),
          "data-testid": AccordionTestIds.AccordionRoot,
          ref
        },
        props
      ),
      children
    }
  );
});
AccordionRoot.displayName = "AccordionRoot";

// src/Accordion/components/composed/AccordionTrigger.tsx
import React5 from "react";
import * as RadixAccordion4 from "@radix-ui/react-accordion";
import { cva as cva5 } from "class-variance-authority";

// src/Accordion/components/composed/AccordionIcon.tsx
import React4 from "react";
import { Primitive as Primitive2 } from "@radix-ui/react-primitive";
import { cva as cva4 } from "class-variance-authority";
import { ChevronDown } from "@nv-brand-assets/react-icons-inline";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/accordion.min.css";
import { jsx as jsx4 } from "react/jsx-runtime";
var accordionIcon = cva4("kui-accordion-icon");
var AccordionIcon = React4.forwardRef(({ className, children = /* @__PURE__ */ jsx4(ChevronDown, { variant: "line" }), ...props }, ref) => {
  return /* @__PURE__ */ jsx4(
    Primitive2.div,
    {
      ...mergeProps(
        {
          className: accordionIcon({ className }),
          "data-testid": AccordionTestIds.AccordionIcon,
          ref
        },
        props
      ),
      children
    }
  );
});
AccordionIcon.displayName = "AccordionIcon";

// src/Accordion/components/composed/AccordionTrigger.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/accordion.min.css";
import { jsx as jsx5, jsxs } from "react/jsx-runtime";
var accordionTrigger = cva5("kui-accordion-trigger");
var AccordionTrigger = React5.forwardRef(({ className, children, icon = /* @__PURE__ */ jsx5(AccordionIcon, {}), ...props }, ref) => {
  return /* @__PURE__ */ jsxs(
    RadixAccordion4.Trigger,
    {
      ...mergeProps(
        {
          className: accordionTrigger({ className }),
          "data-testid": AccordionTestIds.AccordionTrigger,
          ref
        },
        props
      ),
      children: [
        /* @__PURE__ */ jsx5("div", { className: "kui-accordion-label-text", children }),
        icon
      ]
    }
  );
});
AccordionTrigger.displayName = "AccordionTrigger";

// src/Accordion/components/base/Accordion.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/accordion.min.css";
import { jsx as jsx6, jsxs as jsxs2 } from "react/jsx-runtime";
var Accordion = ({
  items,
  ...props
}) => /* @__PURE__ */ jsx6(AccordionRoot, { ...props, children: items.map((item) => /* @__PURE__ */ jsxs2(AccordionItem2, { value: item.value, children: [
  /* @__PURE__ */ jsx6(AccordionTrigger, { disabled: item.disabled, children: item.header }),
  /* @__PURE__ */ jsx6(AccordionContent, { children: item.content })
] }, item.value)) });

// src/AppBar/components/base/AppBar.tsx
import { forwardRef } from "react";
import { cva as cva7 } from "class-variance-authority";

// src/Logo/components/base/Logo.tsx
import React6 from "react";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/logo.min.css";
import { cva as cva6 } from "class-variance-authority";

// src/Logo/constands.ts
var LogoTestIds = {
  Root: "kui-logo-root",
  Logo: "kui-logo-element"
};

// src/Logo/svgs/Solid.tsx
import { jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var Solid = (props) => /* @__PURE__ */ jsxs3("svg", { ...props, viewBox: "0 0 164 30", children: [
  /* @__PURE__ */ jsx7("defs", { children: /* @__PURE__ */ jsx7("path", { d: "M157.725 29.99H.01V.048h157.715z" }) }),
  /* @__PURE__ */ jsxs3("g", { fill: "none", fillRule: "evenodd", children: [
    /* @__PURE__ */ jsx7(
      "path",
      {
        d: "M160.352 24.069v-.449h.288c.157 0 .371.012.371.204 0 .208-.11.245-.296.245h-.363m0 .315h.192l.447.784h.49l-.494-.816c.255-.019.465-.14.465-.484 0-.427-.295-.565-.793-.565h-.721v1.865h.414v-.784m2.098-.146c0-1.095-.851-1.73-1.8-1.73-.955 0-1.805.635-1.805 1.73s.85 1.733 1.805 1.733c.948 0 1.8-.638 1.8-1.733m-.52 0c0 .798-.587 1.334-1.28 1.334v-.006c-.713.006-1.289-.53-1.289-1.328 0-.797.577-1.331 1.289-1.331.694 0 1.28.534 1.28 1.331",
        className: "kui-logo-path"
      }
    ),
    /* @__PURE__ */ jsx7(
      "path",
      {
        d: "M96.374 5.707l.002 19.66h5.552V5.707h-5.554zm-43.677-.026v19.686H58.3V10.086l4.37.014c1.437 0 2.43.345 3.123 1.084.879.936 1.237 2.444 1.237 5.205v8.978h5.427V14.49c0-7.763-4.948-8.81-9.789-8.81h-9.97zm52.617.027v19.659h9.006c4.798 0 6.364-.798 8.057-2.587 1.198-1.256 1.971-4.014 1.971-7.027 0-2.763-.655-5.228-1.797-6.763-2.057-2.745-5.02-3.282-9.445-3.282h-7.792zm5.508 4.28h2.387c3.463 0 5.703 1.556 5.703 5.591 0 4.037-2.24 5.592-5.703 5.592h-2.387V9.989zm-22.453-4.28l-4.634 15.58-4.44-15.579-5.993-.001 6.34 19.659h8.003l6.391-19.659H88.37zm38.563 19.659h5.553V5.709l-5.555-.001.002 19.659zm15.564-19.652l-7.753 19.645h5.475l1.227-3.472h9.175l1.161 3.472h5.944l-7.812-19.646-7.417.001zM146.1 9.3l3.364 9.204h-6.833l3.47-9.204zM16.889 8.985V6.28c.262-.02.528-.033.798-.042 7.4-.232 12.255 6.359 12.255 6.359s-5.244 7.282-10.866 7.282a6.82 6.82 0 01-2.187-.35v-8.204c2.88.348 3.46 1.62 5.192 4.508l3.852-3.248s-2.812-3.688-7.552-3.688c-.515 0-1.008.036-1.492.088zm0-8.938V4.09c.265-.021.531-.038.798-.048 10.29-.346 16.995 8.44 16.995 8.44s-7.7 9.364-15.723 9.364c-.735 0-1.424-.068-2.07-.183v2.498c.553.07 1.126.112 1.724.112 7.465 0 12.864-3.812 18.092-8.325.867.694 4.416 2.383 5.145 3.123-4.971 4.16-16.555 7.515-23.123 7.515a18.89 18.89 0 01-1.838-.096V30h28.375V.047H16.89zm0 19.482v2.133c-6.905-1.23-8.822-8.408-8.822-8.408s3.316-3.674 8.822-4.269v2.34l-.011-.001c-2.89-.347-5.147 2.353-5.147 2.353s1.265 4.544 5.158 5.852zM4.625 12.943s4.092-6.04 12.264-6.663V4.088C7.838 4.815 0 12.48 0 12.48s4.439 12.833 16.889 14.008V24.16C7.753 23.011 4.625 12.943 4.625 12.943z",
        className: "kui-logo-path"
      }
    )
  ] })
] });

// src/Logo/svgs/Standard.tsx
import { jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
var Standard = (props) => /* @__PURE__ */ jsxs4("svg", { ...props, viewBox: "0 0 164 30", children: [
  /* @__PURE__ */ jsx8("defs", { children: /* @__PURE__ */ jsx8("path", { d: "M157.725 29.99H.01V.048h157.715z" }) }),
  /* @__PURE__ */ jsxs4("g", { fill: "none", fillRule: "evenodd", children: [
    /* @__PURE__ */ jsx8(
      "path",
      {
        d: "M160.352 24.069v-.449h.288c.157 0 .371.012.371.204 0 .208-.11.245-.296.245h-.363m0 .315h.192l.447.784h.49l-.494-.816c.255-.019.465-.14.465-.484 0-.427-.295-.565-.793-.565h-.721v1.865h.414v-.784m2.098-.146c0-1.095-.851-1.73-1.8-1.73-.955 0-1.805.635-1.805 1.73s.85 1.733 1.805 1.733c.948 0 1.8-.638 1.8-1.733m-.52 0c0 .798-.587 1.334-1.28 1.334v-.006c-.713.006-1.289-.53-1.289-1.328 0-.797.577-1.331 1.289-1.331.694 0 1.28.534 1.28 1.331",
        className: "kui-logo-path"
      }
    ),
    /* @__PURE__ */ jsx8(
      "path",
      {
        d: "M96.374 5.707l.002 19.66h5.552V5.707h-5.554zm-43.677-.026v19.686H58.3V10.086l4.37.014c1.437 0 2.43.345 3.123 1.084.879.936 1.237 2.444 1.237 5.205v8.978h5.427V14.49c0-7.763-4.948-8.81-9.789-8.81h-9.97zm52.617.027v19.659h9.006c4.798 0 6.364-.798 8.057-2.587 1.198-1.256 1.971-4.014 1.971-7.027 0-2.763-.655-5.228-1.797-6.763-2.057-2.745-5.02-3.282-9.445-3.282h-7.792zm5.508 4.28h2.387c3.463 0 5.703 1.556 5.703 5.591 0 4.037-2.24 5.592-5.703 5.592h-2.387V9.989zm-22.453-4.28l-4.634 15.58-4.44-15.579-5.993-.001 6.34 19.659h8.003l6.391-19.659H88.37zm38.563 19.659h5.553V5.709l-5.555-.001.002 19.659zm15.564-19.652l-7.753 19.645h5.475l1.227-3.472h9.175l1.161 3.472h5.944l-7.812-19.646-7.417.001zM146.1 9.3l3.364 9.204h-6.833l3.47-9.204z",
        className: "kui-logo-path"
      }
    ),
    /* @__PURE__ */ jsx8(
      "path",
      {
        d: "M16.889 8.985V6.28c.262-.02.528-.033.798-.042 7.4-.232 12.255 6.359 12.255 6.359s-5.244 7.282-10.866 7.282a6.82 6.82 0 01-2.187-.35v-8.204c2.88.348 3.46 1.62 5.192 4.508l3.852-3.248s-2.812-3.688-7.552-3.688c-.515 0-1.008.036-1.492.088zm0-8.938V4.09c.265-.021.531-.038.798-.048 10.29-.346 16.995 8.44 16.995 8.44s-7.7 9.364-15.723 9.364c-.735 0-1.424-.068-2.07-.183v2.498c.553.07 1.126.112 1.724.112 7.465 0 12.864-3.812 18.092-8.325.867.694 4.416 2.383 5.145 3.123-4.971 4.16-16.555 7.515-23.123 7.515a18.89 18.89 0 01-1.838-.096V30h28.375V.047H16.89zm0 19.482v2.133c-6.905-1.23-8.822-8.408-8.822-8.408s3.316-3.674 8.822-4.269v2.34l-.011-.001c-2.89-.347-5.147 2.353-5.147 2.353s1.265 4.544 5.158 5.852zM4.625 12.943s4.092-6.04 12.264-6.663V4.088C7.838 4.815 0 12.48 0 12.48s4.439 12.833 16.889 14.008V24.16C7.753 23.011 4.625 12.943 4.625 12.943z",
        fill: "#76B900"
      }
    )
  ] })
] });

// src/Logo/svgs/Compact.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var Compact = (props) => /* @__PURE__ */ jsx9("svg", { ...props, viewBox: "0 0 99 75", className: "kui-logo-element", children: /* @__PURE__ */ jsx9(
  "path",
  {
    d: "M13.246 20.365s6.442-9.537 19.304-10.523v-3.46C18.304 7.53 5.967 19.636 5.967 19.636S12.954 39.904 32.55 41.76v-3.678c-14.38-1.816-19.304-17.717-19.304-17.717zM32.55 30.768v3.368c-10.868-1.944-13.885-13.28-13.885-13.28s5.219-5.8 13.885-6.74v3.695l-.016-.002c-4.548-.548-8.102 3.716-8.102 3.716s1.99 7.177 8.118 9.243zM32.55 0v6.382c.419-.033.837-.06 1.257-.075 16.198-.547 26.751 13.329 26.751 13.329S48.436 34.425 35.809 34.425a18.55 18.55 0 01-3.259-.289v3.946a21.4 21.4 0 002.714.176c11.751 0 20.25-6.02 28.479-13.148 1.363 1.097 6.949 3.763 8.098 4.932-7.825 6.571-26.059 11.869-36.396 11.869-.997 0-1.955-.06-2.895-.15v5.544h44.664V0H32.55zm0 14.115V9.842c.414-.03.832-.051 1.257-.065 11.648-.367 19.29 10.042 19.29 10.042S44.843 31.32 35.994 31.32a10.71 10.71 0 01-3.444-.552V17.81c4.535.55 5.447 2.56 8.173 7.118l6.063-5.129s-4.425-5.824-11.886-5.824c-.812 0-1.588.057-2.35.14zm65.847 58.283c0 .728-.528 1.217-1.153 1.217v-.005c-.642.005-1.16-.484-1.16-1.212s.518-1.216 1.16-1.216c.625 0 1.153.488 1.153 1.216m.469 0c0-1-.767-1.58-1.622-1.58-.86 0-1.627.58-1.627 1.58 0 .998.766 1.58 1.627 1.58.855 0 1.622-.582 1.622-1.58m-1.892.132h.175l.402.716h.442l-.446-.745c.23-.016.42-.128.42-.442 0-.39-.266-.515-.715-.515h-.65v1.702h.372v-.716m0-.287v-.41h.26c.142 0 .336.012.336.186 0 .19-.1.224-.267.224h-.329M84.162 58.756l3.03 8.399h-6.157l3.127-8.4zm-3.248-3.27l-6.986 17.928h4.933l1.105-3.169h8.268l1.046 3.17h5.355l-7.039-17.931-6.682.001zM66.89 73.42h5.004V55.48l-5.005-.002.001 17.942zM32.142 55.478l-4.175 14.22-4-14.218-5.401-.002 5.713 17.942h7.211l5.759-17.942h-5.107zm20.232 3.907h2.15c3.121 0 5.139 1.42 5.139 5.103s-2.018 5.103-5.138 5.103h-2.151V59.385zm-4.963-3.907V73.42h8.114c4.323 0 5.735-.729 7.26-2.361 1.08-1.146 1.776-3.663 1.776-6.412 0-2.522-.59-4.773-1.62-6.173-1.853-2.506-4.523-2.996-8.509-2.996h-7.02zM0 55.453V73.42h5.047V59.778l3.91.001c1.296 0 2.218.327 2.842 1 .791.855 1.114 2.233 1.114 4.752v7.89h4.89v-9.927c0-7.085-4.458-8.04-8.82-8.04H0zm39.355.025l.002 17.942h5.003V55.478h-5.005z",
    className: "kui-logo-path",
    fillRule: "evenodd"
  }
) });

// src/Logo/svgs/Image.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
var Image = (props) => /* @__PURE__ */ jsx10("svg", { ...props, className: "kui-logo-element", viewBox: "0 0 71 47", children: /* @__PURE__ */ jsx10(
  "path",
  {
    d: "M7.255 20.234s6.419-9.476 19.236-10.455V6.34C12.294 7.481 0 19.51 0 19.51s6.963 20.138 26.491 21.982v-3.655C12.161 36.032 7.255 20.234 7.255 20.234zM26.49 30.57v3.346c-10.83-1.931-13.837-13.194-13.837-13.194s5.2-5.764 13.837-6.698v3.672l-.016-.002c-4.532-.544-8.074 3.692-8.074 3.692s1.984 7.131 8.09 9.184zm0-30.57v6.341c.417-.033.834-.06 1.253-.074 16.14-.544 26.658 13.242 26.658 13.242s-12.08 14.694-24.663 14.694c-1.153 0-2.234-.107-3.248-.287v3.92a21.24 21.24 0 002.704.176c11.71 0 20.18-5.982 28.38-13.063 1.359 1.089 6.925 3.738 8.07 4.9-7.797 6.529-25.968 11.792-36.27 11.792-.993 0-1.947-.06-2.884-.15V47H71V0H26.491zm0 14.024V9.78c.412-.03.829-.052 1.253-.065 11.607-.365 19.222 9.977 19.222 9.977S38.742 31.12 29.923 31.12c-1.27 0-2.407-.205-3.432-.55V17.697c4.52.546 5.428 2.543 8.145 7.073l6.042-5.096s-4.41-5.787-11.845-5.787c-.81 0-1.583.057-2.342.138z",
    className: "kui-logo-path"
  }
) });

// src/Logo/components/base/Logo.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
var logo = cva6("kui-logo-root", {
  variants: {
    kind: {
      standard: "kui-logo--kind-standard",
      solid: "kui-logo--kind-solid",
      compact: "kui-logo--kind-compact",
      "image-only": "kui-logo--kind-image-only"
    }
  },
  defaultVariants: {
    kind: "standard"
  }
});
var logoMap = {
  standard: Standard,
  solid: Solid,
  compact: Compact,
  "image-only": Image
};
var Logo = React6.forwardRef(
  ({ kind = "standard", className, ...props }, ref) => {
    return /* @__PURE__ */ jsx11(
      "div",
      {
        ...mergeProps(
          {
            className: logo({ className, kind }),
            ref,
            "data-testid": LogoTestIds.Root
          },
          props
        ),
        children: kind && React6.createElement(logoMap[kind], {
          "data-testid": LogoTestIds.Logo
        })
      }
    );
  }
);
Logo.displayName = "Logo";

// src/AppBar/components/base/AppBar.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/app-bar.min.css";

// src/AppBar/constants.ts
var AppBarTestIds = {
  AppBarRoot: "kui-app-bar-root",
  AppBarSlotLeft: "kui-app-bar-slot-left",
  AppBarSlotCenter: "kui-app-bar-slot-center",
  AppBarSlotRight: "kui-app-bar-slot-right"
};

// src/AppBar/components/base/AppBar.tsx
import { jsx as jsx12, jsxs as jsxs5 } from "react/jsx-runtime";
var appbar = cva7("kui-app-bar-root");
var AppBar = forwardRef(
  ({ className, slotLeft = /* @__PURE__ */ jsx12(Logo, {}), slotCenter, slotRight, ...props }, ref) => {
    return /* @__PURE__ */ jsxs5(
      "div",
      {
        ...mergeProps(
          {
            className: appbar({ className }),
            ref,
            "data-testid": AppBarTestIds.AppBarRoot
          },
          props
        ),
        children: [
          /* @__PURE__ */ jsx12(
            "div",
            {
              className: "kui-app-bar-slot-left",
              "data-testid": AppBarTestIds.AppBarSlotLeft,
              children: slotLeft
            }
          ),
          slotCenter && /* @__PURE__ */ jsx12(
            "div",
            {
              className: "kui-app-bar-slot-center",
              "data-testid": AppBarTestIds.AppBarSlotCenter,
              children: slotCenter
            }
          ),
          slotRight && /* @__PURE__ */ jsx12(
            "div",
            {
              className: "kui-app-bar-slot-right",
              "data-testid": AppBarTestIds.AppBarSlotRight,
              children: slotRight
            }
          )
        ]
      }
    );
  }
);
AppBar.displayName = "AppBar";

// src/Avatar/components/base/Avatar.tsx
import React10 from "react";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/avatar.min.css";

// src/Avatar/components/composed/AvatarFallback.tsx
import React7 from "react";
import * as RadixAvatar from "@radix-ui/react-avatar";
import { cva as cva8 } from "class-variance-authority";

// src/Avatar/constants.ts
var AvatarTestIds = {
  Avatar: "kui-avatar",
  AvatarFallback: "kui-avatar-fallback",
  AvatarImage: "kui-avatar-image",
  AvatarRoot: "kui-avatar-root"
};

// src/Avatar/components/composed/AvatarFallback.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
var avatarFallback = cva8("kui-avatar-fallback");
var AvatarFallback = React7.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx13(
    RadixAvatar.Fallback,
    {
      ...mergeProps(
        {
          className: avatarFallback({ className }),
          "data-testid": AvatarTestIds.AvatarFallback,
          ref
        },
        props
      )
    }
  );
});
AvatarFallback.displayName = "AvatarFallback";

// src/Avatar/components/composed/AvatarImage.tsx
import React8 from "react";
import * as RadixAvatar2 from "@radix-ui/react-avatar";
import { cva as cva9 } from "class-variance-authority";
import { jsx as jsx14 } from "react/jsx-runtime";
var avatarImage = cva9("kui-avatar-image");
var AvatarImage = React8.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx14(
      RadixAvatar2.Image,
      {
        ...mergeProps(
          {
            className: avatarImage({ className }),
            "data-testid": AvatarTestIds.AvatarImage,
            ref
          },
          props
        )
      }
    );
  }
);
AvatarImage.displayName = "AvatarImage";

// src/Avatar/components/composed/AvatarRoot.tsx
import React9 from "react";
import * as RadixAvatar3 from "@radix-ui/react-avatar";
import { cva as cva10 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/avatar.min.css";
import { jsx as jsx15 } from "react/jsx-runtime";
var avatar = cva10("kui-avatar-root", {
  variants: {
    size: {
      small: "kui-avatar-root--size-small",
      medium: "kui-avatar-root--size-medium",
      large: "kui-avatar-root--size-large",
      ["x-large"]: "kui-avatar-root--size-x-large",
      ["xx-large"]: "kui-avatar-root--size-xx-large"
    },
    interactive: {
      true: "kui-avatar-root--interactive"
    }
  },
  defaultVariants: {
    size: "medium"
  }
});
var AvatarRoot = React9.forwardRef(
  ({ className, size, interactive, ...props }, ref) => {
    const isInteractive = interactive !== void 0 ? interactive : !!props.onClick;
    return /* @__PURE__ */ jsx15(
      RadixAvatar3.Avatar,
      {
        ...mergeProps(
          {
            className: avatar({
              className,
              size,
              interactive: isInteractive
            }),
            "data-testid": AvatarTestIds.AvatarRoot,
            ref
          },
          props
        )
      }
    );
  }
);
AvatarRoot.displayName = "AvatarRoot";

// src/Avatar/components/base/Avatar.tsx
import { jsx as jsx16, jsxs as jsxs6 } from "react/jsx-runtime";
var Avatar2 = React10.forwardRef(
  ({ className, src, fallback, alt, ...props }, ref) => {
    return /* @__PURE__ */ jsxs6(AvatarRoot, { ...mergeProps({ className, ref }, props), children: [
      /* @__PURE__ */ jsx16(AvatarImage, { src, alt }),
      /* @__PURE__ */ jsx16(AvatarFallback, { children: fallback })
    ] });
  }
);
Avatar2.displayName = "Avatar";

// src/Badge/components/base/Badge.tsx
import { forwardRef as forwardRef2 } from "react";
import { Primitive as Primitive3 } from "@radix-ui/react-primitive";
import { cva as cva11 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/badge.min.css";
import { jsx as jsx17 } from "react/jsx-runtime";
var badge = cva11("kui-badge", {
  variants: {
    kind: {
      outline: "kui-badge--kind-outline",
      solid: "kui-badge--kind-solid",
      bold: "kui-badge--kind-bold"
    },
    color: {
      blue: "kui-badge--color-blue",
      green: "kui-badge--color-green",
      red: "kui-badge--color-red",
      yellow: "kui-badge--color-yellow",
      purple: "kui-badge--color-purple",
      teal: "kui-badge--color-teal"
    }
  },
  defaultVariants: {
    kind: "outline",
    color: "blue"
  }
});
var badgeTestId = "kui-badge";
var Badge = forwardRef2(
  ({ className, color, kind, type, ...props }, ref) => {
    return /* @__PURE__ */ jsx17(
      Primitive3.span,
      {
        ...mergeProps(
          {
            className: badge({ className, kind: kind || type, color }),
            ref,
            "data-testid": badgeTestId
          },
          props
        )
      }
    );
  }
);
Badge.displayName = "Badge";

// src/Banner/components/base/Banner.tsx
import { createElement } from "react";

// src/Banner/components/composed/BannerCloseButton.tsx
import React11 from "react";
import { Primitive as Primitive4 } from "@radix-ui/react-primitive";
import { cva as cva12 } from "class-variance-authority";
import { Close } from "@nv-brand-assets/react-icons-inline";

// src/Banner/constants.ts
var BannerTestIds = {
  BannerContent: "kui-banner-content",
  BannerTextGroup: "kui-banner-text-group",
  BannerTitle: "kui-banner-title",
  BannerRoot: "kui-banner-root",
  BannerActionGroup: "kui-banner-action-group",
  BannerAction: "kui-banner-action-",
  BannerText: "kui-banner-text",
  BannerIcon: "kui-banner-icon",
  BannerCloseButton: "kui-banner-close-button"
};

// src/Banner/components/composed/BannerCloseButton.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/banner.min.css";
import { jsx as jsx18 } from "react/jsx-runtime";
var bannerCloseButton = cva12("kui-banner-close-button");
var BannerCloseButton = React11.forwardRef(({ className, children = /* @__PURE__ */ jsx18(Close, {}), ...props }, ref) => {
  return /* @__PURE__ */ jsx18(
    Primitive4.button,
    {
      ...mergeProps(
        {
          className: bannerCloseButton({ className }),
          "data-testid": BannerTestIds.BannerCloseButton,
          "aria-label": "close-button",
          ref
        },
        props
      ),
      children
    }
  );
});
BannerCloseButton.displayName = "BannerCloseButton";

// src/Banner/components/composed/BannerContent.tsx
import React12 from "react";
import { Primitive as Primitive5 } from "@radix-ui/react-primitive";
import { cva as cva13 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/banner.min.css";
import { jsx as jsx19 } from "react/jsx-runtime";
var bannerContent = cva13("kui-banner-content", {
  variants: {
    actionsPosition: {
      right: "kui-banner-content--actionsPosition-right",
      bottom: "kui-banner-content-actionsPosition-bottom"
    }
  },
  defaultVariants: {
    actionsPosition: "right"
  }
});
var BannerContent = React12.forwardRef(({ actionsPosition, children, className, ...props }, ref) => {
  return /* @__PURE__ */ jsx19(
    Primitive5.div,
    {
      ...mergeProps(
        {
          className: bannerContent({ className, actionsPosition }),
          "data-testid": BannerTestIds.BannerContent,
          ref
        },
        props
      ),
      children
    }
  );
});
BannerContent.displayName = "BannerContent";

// src/Banner/components/composed/BannerDescription.tsx
import React13 from "react";
import { Primitive as Primitive6 } from "@radix-ui/react-primitive";
import { cva as cva14 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/banner.min.css";
import { jsx as jsx20 } from "react/jsx-runtime";
var bannerDescription = cva14("kui-banner-description");
var BannerDescription = React13.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx20(
    Primitive6.span,
    {
      ...mergeProps(
        {
          className: bannerDescription({ className }),
          "data-testid": BannerTestIds.BannerText,
          ref
        },
        props
      )
    }
  );
});
BannerDescription.displayName = "BannerDescription";

// src/Banner/components/composed/BannerIcon.tsx
import React14 from "react";
import { Primitive as Primitive7 } from "@radix-ui/react-primitive";
import { cva as cva15 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/banner.min.css";
import { jsx as jsx21 } from "react/jsx-runtime";
var bannerIcon = cva15("kui-banner-icon");
var BannerIcon = React14.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx21(
      Primitive7.div,
      {
        ...mergeProps(
          {
            className: bannerIcon({ className }),
            "data-testid": BannerTestIds.BannerIcon,
            ref
          },
          props
        ),
        children
      }
    );
  }
);
BannerIcon.displayName = "BannerIcon";

// src/Banner/components/composed/BannerRoot.tsx
import React15 from "react";
import { Primitive as Primitive8 } from "@radix-ui/react-primitive";
import { cva as cva16 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/banner.min.css";
import { jsx as jsx22 } from "react/jsx-runtime";
var bannerRoot = cva16("kui-banner-root", {
  variants: {
    status: {
      error: "kui-banner-root--status-error",
      warning: "kui-banner-root--status-warning",
      success: "kui-banner-root--status-success",
      info: "kui-banner-root--status-info"
    },
    kind: {
      global: "kui-banner-root--kind-global",
      header: "kui-banner-root--kind-header",
      inline: "kui-banner-root--kind-inline"
    }
  },
  defaultVariants: {
    kind: "global",
    status: "info"
  }
});
var BannerRoot = React15.forwardRef(
  ({ children, className, kind, status, ...props }, ref) => {
    return /* @__PURE__ */ jsx22(
      Primitive8.div,
      {
        ...mergeProps(
          {
            className: bannerRoot({ className, kind, status }),
            "data-testid": BannerTestIds.BannerRoot,
            ref
          },
          props
        ),
        children
      }
    );
  }
);
BannerRoot.displayName = "BannerRoot";

// src/Banner/components/composed/BannerTextGroup.tsx
import React16 from "react";
import { Primitive as Primitive9 } from "@radix-ui/react-primitive";
import { cva as cva17 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/banner.min.css";
import { jsx as jsx23 } from "react/jsx-runtime";
var bannerTextGroup = cva17("kui-banner-text-group");
var BannerTextGroup = React16.forwardRef(({ children, className, ...props }, ref) => {
  return /* @__PURE__ */ jsx23(
    Primitive9.div,
    {
      ...mergeProps(
        {
          className: bannerTextGroup({ className }),
          "data-testid": BannerTestIds.BannerTextGroup,
          ref
        },
        props
      ),
      children
    }
  );
});
BannerTextGroup.displayName = "BannerTextGroup";

// src/Banner/components/composed/BannerTitle.tsx
import React17 from "react";
import { Primitive as Primitive10 } from "@radix-ui/react-primitive";
import { cva as cva18 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/banner.min.css";
import { jsx as jsx24 } from "react/jsx-runtime";
var bannerTitle = cva18("kui-banner-title", {
  variants: {
    header: {
      true: "kui-banner-title--header"
    }
  }
});
var BannerTitle = React17.forwardRef(
  ({ className, header, ...props }, ref) => {
    return /* @__PURE__ */ jsx24(
      Primitive10.span,
      {
        ...mergeProps(
          {
            className: bannerTitle({ className, header }),
            "data-testid": BannerTestIds.BannerTitle,
            ref
          },
          props
        )
      }
    );
  }
);
BannerTitle.displayName = "BannerTitle";

// src/Banner/components/base/Banner.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/banner.min.css";
import { jsx as jsx25, jsxs as jsxs7 } from "react/jsx-runtime";
var Banner = (props) => {
  return /* @__PURE__ */ jsxs7(BannerRoot, { kind: props.kind, status: props.status, children: [
    /* @__PURE__ */ jsxs7(BannerContent, { actionsPosition: props.actionsPosition, children: [
      props.icon && /* @__PURE__ */ jsx25(BannerIcon, { children: createElement(props.icon, {
        variant: "fill"
      }) }),
      /* @__PURE__ */ jsxs7(BannerTextGroup, { children: [
        props.title && /* @__PURE__ */ jsx25(BannerTitle, { header: props.kind === "header", children: props.title }),
        props.kind === "header" && props.description && /* @__PURE__ */ jsx25(BannerDescription, { children: props.description })
      ] })
    ] }),
    props.actions,
    props.onClose && /* @__PURE__ */ jsx25(BannerCloseButton, { onClick: props.onClose })
  ] });
};

// src/Banner/components/composed/BannerAction.tsx
import React19 from "react";
import { Primitive as Primitive11 } from "@radix-ui/react-primitive";
import { cva as cva19 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/banner.min.css";
import { jsx as jsx26 } from "react/jsx-runtime";
var bannerAction = cva19("kui-banner-action");
var BannerAction = React19.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx26(
    Primitive11.button,
    {
      ...mergeProps(
        {
          className: bannerAction({ className }),
          "data-testid": BannerTestIds.BannerAction,
          ref
        },
        props
      )
    }
  );
});
BannerAction.displayName = "BannerAction";

// src/Block/components/base/Block.tsx
import React20 from "react";
import { cva as cva21, cx } from "class-variance-authority";

// src/lib/components/primitive.ts
import { cva as cva20 } from "class-variance-authority";

// src/lib/constants/properties.ts
var AlignItemsValues = [
  "baseline",
  "center",
  "start",
  "end",
  "stretch"
];
var DirectionValues = ["row", "col"];
var JustifyContentValues = [
  "normal",
  "start",
  "end",
  "center",
  "between",
  "around",
  "evenly",
  "stretch"
];
var OverflowValues = [
  "auto",
  "clip",
  "hidden",
  "scroll",
  "visible"
];
var SizeValues = [
  "none",
  "xxs",
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl",
  "3xl",
  "4xl"
];
var TextOverflowValues = ["ellipsis", "clip"];
var TextWrapValues = ["wrap", "nowrap", "balance", "pretty"];

// src/lib/utils/variants.ts
var getVariantClassName = (base, variant, value, screen) => {
  const parts = [`${base}--${variant}-${value}`];
  if (screen) {
    parts.unshift(screen);
  }
  return parts.join(":");
};
var buildVariants = (base, variant, values) => values.reduce(
  (map, value) => {
    map[value] = `${base}--${variant}-${value}`;
    return map;
  },
  {}
);

// src/lib/components/primitive.ts
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/primitive.min.css";
import { Primitive as Primitive12 } from "@radix-ui/react-primitive";
var primitive = cva20("", {
  variants: {
    gap: buildVariants("kui-primitive", "gap", SizeValues),
    spacing: buildVariants("kui-primitive", "spacing", SizeValues),
    spacingX: buildVariants("kui-primitive", "spacing-x", SizeValues),
    spacingY: buildVariants("kui-primitive", "spacing-y", SizeValues)
  },
  defaultVariants: {
    spacing: "none"
  }
});

// src/Block/constants.ts
var BlockTestIds = {
  Block: "kui-block"
};

// src/Block/components/base/Block.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/block.min.css";
import { jsx as jsx27 } from "react/jsx-runtime";
var block = cva21("kui-block", {
  variants: {
    overflow: buildVariants("kui-block", "overflow", OverflowValues),
    overflowX: buildVariants("kui-block", "overflow-x", OverflowValues),
    overflowY: buildVariants("kui-block", "overflow-y", OverflowValues),
    textOverflow: buildVariants("kui-block", "text", TextOverflowValues),
    textWrap: buildVariants("kui-block", "text", TextWrapValues),
    truncate: {
      true: "kui-block--overflow-hidden kui-block--text-ellipsis kui-block--text-nowrap"
    }
  },
  defaultVariants: {
    overflow: "auto"
  }
});
var Block = React20.forwardRef(
  ({
    className,
    overflow,
    overflowX,
    overflowY,
    spacing,
    spacingX,
    spacingY,
    textOverflow,
    textWrap,
    truncate,
    ...props
  }, ref) => /* @__PURE__ */ jsx27(
    Primitive12.div,
    {
      ...mergeProps(
        {
          className: cx(
            primitive({ className, spacing, spacingX, spacingY }),
            block({
              className,
              overflow,
              overflowX,
              overflowY,
              textOverflow,
              textWrap,
              truncate
            })
          ),
          ref,
          "data-testid": BlockTestIds.Block
        },
        props
      )
    }
  )
);
Block.displayName = "Block";

// src/Breadcrumbs/components/base/Breadcrumbs.tsx
import React25 from "react";

// src/Breadcrumbs/components/composed/BreadcrumbCurrentItem.tsx
import React21 from "react";
import { Primitive as Primitive13 } from "@radix-ui/react-primitive";
import { cva as cva22 } from "class-variance-authority";
import { jsx as jsx28 } from "react/jsx-runtime";
var breadcrumbCurrentItem = cva22("kui-breadcrumbs-current-item");
var BreadcrumbCurrentItem = React21.forwardRef(({ children, className, ...props }, ref) => {
  return /* @__PURE__ */ jsx28(
    Primitive13.span,
    {
      ...mergeProps(
        { className: breadcrumbCurrentItem({ className }), ref },
        props
      ),
      children
    }
  );
});
BreadcrumbCurrentItem.displayName = "BreadcrumbCurrentItem";

// src/Breadcrumbs/components/composed/BreadcrumbItem.tsx
import React22 from "react";
import { Primitive as Primitive14 } from "@radix-ui/react-primitive";
import { cva as cva23 } from "class-variance-authority";
import { jsx as jsx29 } from "react/jsx-runtime";
var breadcrumbItem = cva23(
  "kui-breadcrumbs-item",
  // Base class
  {
    variants: {
      active: {
        true: "font-bold font-secondary",
        // Active state styles
        false: "font-secondary hover:underline"
        // Inactive state styles
      }
    },
    defaultVariants: {
      active: false
      // Default to inactive state as a boolean
    }
  }
);
var BreadcrumbItem = React22.forwardRef(({ children, active = false, className, ...props }, ref) => {
  return /* @__PURE__ */ jsx29(
    Primitive14.div,
    {
      ...mergeProps(
        { className: breadcrumbItem({ active, className }), ref },
        props
      ),
      children
    }
  );
});
BreadcrumbItem.displayName = "BreadcrumbItem";

// src/Breadcrumbs/components/composed/BreadcrumbSeparator.tsx
import React23 from "react";
import { Primitive as Primitive15 } from "@radix-ui/react-primitive";
import { cva as cva24 } from "class-variance-authority";
import { ChevronRight } from "@nv-brand-assets/react-icons-inline";
import { jsx as jsx30 } from "react/jsx-runtime";
var breadcrumbSeparator = cva24("kui-breadcrumbs-separator");
var BreadcrumbSeparator = React23.forwardRef(({ icon, className, ...props }, ref) => {
  return /* @__PURE__ */ jsx30(
    Primitive15.div,
    {
      ...mergeProps(
        { className: breadcrumbSeparator({ className }), ref },
        props
      ),
      children: icon || /* @__PURE__ */ jsx30(ChevronRight, {})
    }
  );
});
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";

// src/Breadcrumbs/components/composed/BreadcrumbsRoot.tsx
import React24 from "react";
import { Primitive as Primitive16 } from "@radix-ui/react-primitive";
import { cva as cva25 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/breadcrumbs.min.css";
import { jsx as jsx31 } from "react/jsx-runtime";
var breadcrumbsRoot = cva25("kui-breadcrumbs-root");
var BreadcrumbsRoot = React24.forwardRef(({ children, className, ...props }, ref) => {
  return /* @__PURE__ */ jsx31(
    Primitive16.div,
    {
      ...mergeProps({ className: breadcrumbsRoot({ className }), ref }, props),
      children
    }
  );
});
BreadcrumbsRoot.displayName = "BreadcrumbsRoot";

// src/Breadcrumbs/components/base/Breadcrumbs.tsx
import { Fragment, jsx as jsx32, jsxs as jsxs8 } from "react/jsx-runtime";
var Breadcrumbs = React25.forwardRef(
  ({ items, separator, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx32(BreadcrumbsRoot, { ...mergeProps({ className, ref }, props), children: items.map((item, index) => /* @__PURE__ */ jsx32(React25.Fragment, { children: index < items.length - 1 ? /* @__PURE__ */ jsxs8(Fragment, { children: [
      typeof item.to === "string" ? /* @__PURE__ */ jsx32("a", { href: item.to, className: "kui-breadcrumbs-item", children: item.label }) : /* @__PURE__ */ jsx32(BreadcrumbItem, { children: item.to }),
      /* @__PURE__ */ jsx32(BreadcrumbSeparator, { icon: separator })
    ] }) : /* @__PURE__ */ jsx32(BreadcrumbCurrentItem, { children: item.label }) }, index)) });
  }
);
Breadcrumbs.displayName = "Breadcrumbs";

// src/Button/components/base/Button.tsx
import React26 from "react";
import { Primitive as Primitive17 } from "@radix-ui/react-primitive";
import { cva as cva26 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/button.min.css";

// src/Button/constants.ts
var ButtonTestIds = {
  Button: "kui-button"
};

// src/Button/components/base/Button.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var button = cva26("kui-button", {
  variants: {
    size: {
      large: "kui-button--size-large",
      small: "kui-button--size-small",
      medium: "kui-button--size-medium",
      tiny: "kui-button--size-tiny"
    },
    kind: {
      primary: "kui-button--kind-primary",
      secondary: "kui-button--kind-secondary",
      tertiary: "kui-button--kind-tertiary"
    },
    color: {
      brand: "kui-button--color-brand",
      neutral: "kui-button--color-neutral",
      black: "kui-button--color-black",
      white: "kui-button--color-white"
    },
    danger: {
      true: "kui-button--danger"
    }
  },
  defaultVariants: {
    danger: false,
    kind: "primary",
    size: "medium",
    color: "brand"
  }
});
var Button = React26.forwardRef(
  ({ tone, className, danger, kind, size, children, color, asChild, ...props }, ref) => {
    const wrappedChildren = !asChild ? React26.Children.map(
      children,
      (child) => typeof child === "string" ? /* @__PURE__ */ jsx33("span", { children: child }) : child
    ) : children;
    return /* @__PURE__ */ jsx33(
      Primitive17.button,
      {
        ...mergeProps(
          {
            className: button({
              className,
              danger,
              kind: kind || tone,
              size,
              color
            }),
            ref,
            "data-testid": ButtonTestIds.Button,
            asChild
          },
          props
        ),
        children: wrappedChildren
      }
    );
  }
);
Button.displayName = "Button";

// src/Card/components/composed/CardContent.tsx
import React27 from "react";
import { Primitive as Primitive18 } from "@radix-ui/react-primitive";
import { cva as cva27 } from "class-variance-authority";

// src/Card/constants.ts
var CardTestIds = {
  CardRoot: "kui-card-root",
  CardContent: "kui-card-content",
  CardMedia: "kui-card-media"
};

// src/Card/components/composed/CardContent.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/card.min.css";
import { jsx as jsx34 } from "react/jsx-runtime";
var cardContent = cva27("kui-card-content");
var CardContent = React27.forwardRef(
  ({ children, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx34(
      Primitive18.div,
      {
        ...mergeProps(
          {
            className: cardContent({ className }),
            "data-testid": CardTestIds.CardContent,
            ref
          },
          props
        ),
        children
      }
    );
  }
);
CardContent.displayName = "CardContent";

// src/Card/components/composed/CardMedia.tsx
import React28 from "react";
import { Primitive as Primitive19 } from "@radix-ui/react-primitive";
import { cva as cva28 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/card.min.css";
import { jsx as jsx35, jsxs as jsxs9 } from "react/jsx-runtime";
var cardMedia = cva28("kui-card-media");
var CardMedia = React28.forwardRef(
  ({ children, className, header, ...props }, ref) => {
    return /* @__PURE__ */ jsxs9(
      Primitive19.div,
      {
        ...mergeProps(
          {
            className: cardMedia({ className }),
            "data-testid": CardTestIds.CardMedia,
            ref
          },
          props
        ),
        children: [
          header && /* @__PURE__ */ jsx35(Primitive19.div, { className: "kui-card-media-header", children: header }),
          children
        ]
      }
    );
  }
);
CardMedia.displayName = "CardMedia";

// src/Card/components/composed/CardRoot.tsx
import React29 from "react";
import { Primitive as Primitive20 } from "@radix-ui/react-primitive";
import { cva as cva29 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/card.min.css";
import { jsx as jsx36 } from "react/jsx-runtime";
var cardRoot = cva29("kui-card-root", {
  variants: {
    interactive: {
      true: "kui-card--interactive"
    },
    kind: {
      solid: "kui-card--kind-solid",
      gradient: "kui-card--kind-gradient",
      float: "kui-card--kind-float"
    },
    layout: {
      horizontal: "kui-card--layout-horizontal",
      vertical: "kui-card--layout-vertical"
    },
    selected: {
      true: "kui-card--selected"
    }
  },
  defaultVariants: {
    interactive: false,
    kind: "solid",
    layout: "vertical",
    selected: false
  }
});
var CardRoot = React29.forwardRef(
  ({ children, className, interactive, kind, layout, selected, ...props }, ref) => {
    return /* @__PURE__ */ jsx36(
      Primitive20.div,
      {
        ...mergeProps(
          {
            className: cardRoot({
              className,
              interactive,
              layout,
              kind,
              selected
            }),
            "data-testid": CardTestIds.CardRoot,
            ref
          },
          props
        ),
        children
      }
    );
  }
);
CardRoot.displayName = "CardRoot";

// src/Card/components/base/Card.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/card.min.css";
import { jsx as jsx37, jsxs as jsxs10 } from "react/jsx-runtime";
var Card = ({
  children,
  slotHeader,
  slotMedia,
  ...props
}) => {
  return /* @__PURE__ */ jsxs10(CardRoot, { ...props, children: [
    slotMedia && /* @__PURE__ */ jsx37(CardMedia, { header: slotHeader, children: slotMedia }),
    /* @__PURE__ */ jsxs10(CardContent, { children: [
      !slotMedia && slotHeader,
      children
    ] })
  ] });
};
Card.displayName = "Card";

// src/Checkbox/components/composed/CheckboxIndicator.tsx
import React30 from "react";
import * as RadixCheckbox from "@radix-ui/react-checkbox";
import { cva as cva30 } from "class-variance-authority";
import { Check, Subtract } from "@nv-brand-assets/react-icons-inline";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/checkbox.min.css";
import { Fragment as Fragment2, jsx as jsx38, jsxs as jsxs11 } from "react/jsx-runtime";
var checkboxIndicator = cva30("kui-checkbox-indicator", {
  variants: {
    error: {
      true: "kui-checkbox-indicator--error"
    },
    kind: {
      box: "kui-checkbox-indicator--kind-box",
      mark: "kui-checkbox-indicator--kind-mark"
    },
    checked: {
      true: "kui-checkbox-indicator--checked-true",
      false: "kui-checkbox-indicator--checked-false",
      indeterminate: "kui-checkbox-indicator--checked-indeterminate"
    }
  },
  defaultVariants: {
    kind: "box"
  }
});
var CheckboxIndicator = React30.forwardRef(({ className, checked, error, kind, ...props }, ref) => {
  const classes = checkboxIndicator({ className, error, kind, checked });
  return /* @__PURE__ */ jsxs11(Fragment2, { children: [
    kind === "mark" && /* @__PURE__ */ jsx38(Fragment2, { children: checked === "indeterminate" ? /* @__PURE__ */ jsx38(Subtract, { className: classes }) : /* @__PURE__ */ jsx38(Check, { className: classes }) }),
    kind === "box" && /* @__PURE__ */ jsx38(RadixCheckbox.Indicator, { ...mergeProps({ ref }, props), children: /* @__PURE__ */ jsx38(
      "svg",
      {
        xmlns: "http://www.w3.org/2000/svg",
        width: "16",
        height: "16",
        viewBox: "0 0 18 18",
        fill: "none",
        className: classes,
        children: checked === "indeterminate" ? /* @__PURE__ */ jsx38("path", { d: "M4 8L12 8", stroke: "currentColor", strokeWidth: "2" }) : /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M3 8L6.18198 11.182L13.253 4.11091",
            stroke: "currentColor",
            strokeWidth: "2"
          }
        )
      }
    ) })
  ] });
});
CheckboxIndicator.displayName = "CheckboxIndicator";

// src/Checkbox/components/composed/CheckboxBox.tsx
import React31 from "react";
import * as RadixCheckbox2 from "@radix-ui/react-checkbox";
import { cva as cva31 } from "class-variance-authority";

// src/Checkbox/constants.ts
var CheckboxTestIds = {
  Root: "kui-checkbox-root",
  Box: "kui-checkbox-box"
};

// src/Checkbox/components/composed/CheckboxBox.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/checkbox.min.css";
import { jsx as jsx39 } from "react/jsx-runtime";
var checkboxBox = cva31("kui-checkbox-box", {
  variants: {
    error: {
      true: "kui-checkbox-box--error"
    },
    kind: {
      box: "kui-checkbox-box--kind-box",
      mark: "kui-checkbox-box--kind-mark"
    }
  },
  defaultVariants: {
    kind: "box"
  }
});
var CheckboxBox = React31.forwardRef(({ className, error, kind, ...props }, ref) => {
  return /* @__PURE__ */ jsx39(
    RadixCheckbox2.Root,
    {
      ...mergeProps(
        {
          className: checkboxBox({ className, error, kind }),
          "data-testid": CheckboxTestIds.Box,
          ref
        },
        props
      )
    }
  );
});
CheckboxBox.displayName = "CheckboxBox";

// src/Checkbox/components/composed/CheckboxRoot.tsx
import React32 from "react";
import { Primitive as Primitive21 } from "@radix-ui/react-primitive";
import { cva as cva32 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/checkbox.min.css";
import { jsx as jsx40 } from "react/jsx-runtime";
var checkboxRoot = cva32("kui-checkbox-root", {
  variants: {
    labelSide: {
      left: "kui-checkbox-root--labelSide-left",
      right: "kui-checkbox-root--labelSide-right"
    }
  },
  defaultVariants: {
    labelSide: "right"
  }
});
var CheckboxRoot = React32.forwardRef(
  ({ className, labelSide, ...props }, ref) => {
    return /* @__PURE__ */ jsx40(
      Primitive21.div,
      {
        ...mergeProps(
          {
            className: checkboxRoot({ className, labelSide }),
            "data-testid": CheckboxTestIds.Root,
            ref
          },
          props
        )
      }
    );
  }
);
CheckboxRoot.displayName = "CheckboxRoot";

// src/Checkbox/components/composed/CheckboxLabel.tsx
import React34 from "react";
import { cva as cva34 } from "class-variance-authority";

// src/Label/constants.ts
var LabelTestId = {
  Label: "kui-label"
};

// src/Label/components/base/Label.tsx
import React33 from "react";
import * as RadixLabel from "@radix-ui/react-label";
import { cva as cva33 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/label.min.css";
import { jsx as jsx41 } from "react/jsx-runtime";
var label = cva33("kui-label", {
  variants: {
    disabled: {
      true: "kui-label--disabled"
    }
  }
});
var Label = React33.forwardRef(
  ({ className, disabled, ...props }, ref) => {
    return /* @__PURE__ */ jsx41(
      RadixLabel.Root,
      {
        ...mergeProps(
          {
            className: label({ className, disabled }),
            "data-testid": LabelTestId.Label,
            ref
          },
          props
        )
      }
    );
  }
);
Label.displayName = "Label";

// src/Checkbox/components/composed/CheckboxLabel.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/checkbox.min.css";
import { jsx as jsx42 } from "react/jsx-runtime";
var checkboxLabel = cva34("kui-checkbox-label", {
  variants: {
    error: {
      true: "kui-checkbox-label--error"
    },
    kind: {
      box: "kui-checkbox-label--kind-box",
      mark: "kui-checkbox-label--kind-mark"
    }
  },
  defaultVariants: {
    kind: "box"
  }
});
var CheckboxLabel = React34.forwardRef(({ className, kind, error, ...props }, ref) => {
  return /* @__PURE__ */ jsx42(
    Label,
    {
      ...mergeProps(
        {
          className: checkboxLabel({ className, kind, error }),
          ref
        },
        props
      )
    }
  );
});
CheckboxLabel.displayName = "CheckboxLabel";

// src/Checkbox/components/base/Checkbox.tsx
import React35 from "react";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/checkbox.min.css";
import { jsx as jsx43, jsxs as jsxs12 } from "react/jsx-runtime";
var Checkbox = React35.forwardRef(
  ({
    labelSide = "right",
    kind = "box",
    className,
    label: label2,
    checked,
    defaultChecked,
    disabled,
    error,
    ...props
  }, ref) => {
    const boxId = React35.useId();
    const labelId = React35.useId();
    return /* @__PURE__ */ jsxs12(CheckboxRoot, { labelSide, children: [
      /* @__PURE__ */ jsx43(
        CheckboxBox,
        {
          "aria-labelledby": labelId,
          ...mergeProps(
            {
              className,
              defaultChecked,
              ref,
              checked,
              disabled,
              error,
              kind,
              id: boxId
            },
            props
          ),
          children: /* @__PURE__ */ jsx43(
            CheckboxIndicator,
            {
              checked: checked ?? defaultChecked,
              error,
              kind
            }
          )
        }
      ),
      label2 && /* @__PURE__ */ jsx43(
        CheckboxLabel,
        {
          id: labelId,
          htmlFor: boxId,
          disabled,
          kind,
          error,
          children: label2
        }
      )
    ] });
  }
);
Checkbox.displayName = "Checkbox";

// src/Flex/components/base/Flex.tsx
import React36 from "react";
import { cva as cva35, cx as cx2 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/flex.min.css";

// src/Flex/constants.ts
var FlexTestIds = {
  Flex: "kui-flex"
};

// src/Flex/components/base/Flex.tsx
import { jsx as jsx44 } from "react/jsx-runtime";
var flex = cva35("kui-flex", {
  variants: {
    align: buildVariants("kui-flex", "align", AlignItemsValues),
    direction: buildVariants("kui-flex", "direction", DirectionValues),
    justify: buildVariants("kui-flex", "justify", JustifyContentValues)
  },
  defaultVariants: {
    align: "stretch",
    direction: "row",
    justify: "start"
  }
});
var Flex = React36.forwardRef(
  ({
    align,
    className,
    direction,
    gap,
    justify,
    spacing,
    spacingX,
    spacingY,
    ...divProps
  }, ref) => {
    return /* @__PURE__ */ jsx44(
      Primitive12.div,
      {
        ...mergeProps(
          {
            className: cx2(
              primitive({ gap, spacing, spacingX, spacingY }),
              flex({
                align,
                className,
                direction,
                justify
              })
            ),
            ref,
            "data-testid": FlexTestIds.Flex
          },
          divProps
        )
      }
    );
  }
);
Flex.displayName = "Flex";

// src/Grid/components/base/Grid.tsx
import React37 from "react";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/grid.min.css";
import { cva as cva36, cx as cx3 } from "class-variance-authority";

// src/lib/utils/responsive.ts
function getResponsiveClassName(base, variant, responsiveValue, defaultValue) {
  const value = responsiveValue ?? defaultValue;
  if (value) {
    if (typeof value === "object") {
      return Object.entries(value).map(
        ([k, v]) => getVariantClassName(base, variant, v, k === "base" ? void 0 : k)
      ).join(" ");
    }
    return getVariantClassName(base, variant, value);
  }
  return void 0;
}

// src/Grid/constants.ts
var GridTesetIds = {
  Grid: "kui-grid"
};

// src/Grid/components/base/Grid.tsx
import { jsx as jsx45 } from "react/jsx-runtime";
var grid = cva36("kui-grid", {
  variants: {
    flow: buildVariants("kui-grid", "flow", [
      "row",
      "col",
      "dense",
      "row-dense",
      "col-dense"
    ])
  }
});
var Grid = React37.forwardRef(
  ({
    className,
    cols,
    flow,
    gap,
    rows,
    spacing,
    spacingX,
    spacingY,
    ...divProps
  }, ref) => /* @__PURE__ */ jsx45(
    Primitive12.div,
    {
      ...mergeProps(
        {
          className: cx3(
            primitive({ gap, spacing, spacingX, spacingY }),
            grid({
              flow
            }),
            getResponsiveClassName("kui-grid", "cols", cols),
            getResponsiveClassName("kui-grid", "rows", rows),
            className
          ),
          ref,
          "data-testid": GridTesetIds.Grid
        },
        divProps
      )
    }
  )
);
Grid.displayName = "Grid";

// src/Grid/components/base/GridItem.tsx
import React38 from "react";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/grid.min.css";
import { cx as cx4 } from "class-variance-authority";
import { jsx as jsx46 } from "react/jsx-runtime";
var gridItemTestId = "kui-grid-item";
var GridItem = React38.forwardRef(
  ({
    className,
    colEnd,
    cols,
    colStart,
    rows,
    rowEnd,
    rowStart,
    spacing,
    spacingX,
    spacingY,
    ...divProps
  }, ref) => /* @__PURE__ */ jsx46(
    Primitive12.div,
    {
      ...mergeProps(
        {
          className: cx4(
            "kui-grid-item",
            primitive({ spacing, spacingX, spacingY }),
            getResponsiveClassName("kui-grid-item", "col-span", cols, 1),
            getResponsiveClassName("kui-grid-item", "row-span", rows),
            getResponsiveClassName("kui-grid-item", "col-start", colStart),
            getResponsiveClassName("kui-grid-item", "col-end", colEnd),
            getResponsiveClassName("kui-grid-item", "row-start", rowStart),
            getResponsiveClassName("kui-grid-item", "row-end", rowEnd),
            className
          ),
          ref,
          "data-testid": gridItemTestId
        },
        divProps
      )
    }
  )
);
GridItem.displayName = "GridItem";

// src/Hero/components/base/Hero.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/hero.min.css";

// src/Hero/components/composed/HeroBody.tsx
import { forwardRef as forwardRef3 } from "react";
import { Primitive as Primitive22 } from "@radix-ui/react-primitive";
import { cva as cva37 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/hero.min.css";

// src/Hero/constants.ts
var HeroTestIds = {
  HeroRoot: "kui-hero-root",
  HeroMedia: "kui-hero-media",
  HeroContent: "kui-hero-content",
  HeroHeader: "kui-hero-header",
  HeroSubHeading: "kui-hero-sub-heading",
  HeroHeading: "kui-hero-heading",
  HeroBody: "kui-hero-body",
  HeroFooter: "kui-hero-footer"
};

// src/Hero/components/composed/HeroBody.tsx
import { jsx as jsx47 } from "react/jsx-runtime";
var heroBody = cva37("kui-hero-body");
var HeroBody = forwardRef3(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx47(
      Primitive22.div,
      {
        ...mergeProps(
          {
            className: heroBody({ className }),
            ref,
            "data-testid": HeroTestIds.HeroBody
          },
          props
        )
      }
    );
  }
);
HeroBody.displayName = "HeroBody";

// src/Hero/components/composed/HeroContent.tsx
import { forwardRef as forwardRef4 } from "react";
import { Primitive as Primitive23 } from "@radix-ui/react-primitive";
import { cva as cva38 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/hero.min.css";
import { jsx as jsx48 } from "react/jsx-runtime";
var heroContent = cva38("kui-hero-content");
var HeroContent = forwardRef4(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx48(
      Primitive23.div,
      {
        ...mergeProps(
          {
            className: heroContent({ className }),
            ref,
            "data-testid": HeroTestIds.HeroContent
          },
          props
        )
      }
    );
  }
);
HeroContent.displayName = "HeroContent";

// src/Hero/components/composed/HeroFooter.tsx
import { forwardRef as forwardRef5 } from "react";
import { Primitive as Primitive24 } from "@radix-ui/react-primitive";
import { cva as cva39 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/hero.min.css";
import { jsx as jsx49 } from "react/jsx-runtime";
var heroFooter = cva39("kui-hero-footer");
var HeroFooter = forwardRef5(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx49(
      Primitive24.div,
      {
        ...mergeProps(
          {
            className: heroFooter({ className }),
            ref,
            "data-testid": HeroTestIds.HeroFooter
          },
          props
        )
      }
    );
  }
);
HeroFooter.displayName = "HeroFooter";

// src/Hero/components/composed/HeroHeader.tsx
import { forwardRef as forwardRef6 } from "react";
import { Primitive as Primitive25 } from "@radix-ui/react-primitive";
import { cva as cva40 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/hero.min.css";
import { jsx as jsx50 } from "react/jsx-runtime";
var heroHeader = cva40("kui-hero-header");
var HeroHeader = forwardRef6(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx50(
      Primitive25.div,
      {
        ...mergeProps(
          {
            className: heroHeader({ className }),
            ref,
            "data-testid": HeroTestIds.HeroHeader
          },
          props
        )
      }
    );
  }
);
HeroHeader.displayName = "HeroHeader";

// src/Hero/components/composed/HeroHeading.tsx
import { forwardRef as forwardRef7 } from "react";
import { Primitive as Primitive26 } from "@radix-ui/react-primitive";
import { cva as cva41 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/hero.min.css";
import { jsx as jsx51 } from "react/jsx-runtime";
var heroHeading = cva41("kui-hero-heading");
var HeroHeading = forwardRef7(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx51(
      Primitive26.div,
      {
        ...mergeProps(
          {
            className: heroHeading({ className }),
            ref,
            "data-testid": HeroTestIds.HeroHeading
          },
          props
        )
      }
    );
  }
);
HeroHeading.displayName = "HeroHeading";

// src/Hero/components/composed/HeroMedia.tsx
import { forwardRef as forwardRef8 } from "react";
import { Primitive as Primitive27 } from "@radix-ui/react-primitive";
import { cva as cva42 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/hero.min.css";
import { jsx as jsx52 } from "react/jsx-runtime";
var heroMedia = cva42("kui-hero-media");
var HeroMedia = forwardRef8(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx52(
      Primitive27.div,
      {
        ...mergeProps(
          {
            className: heroMedia({ className }),
            ref,
            "data-testid": HeroTestIds.HeroMedia
          },
          props
        )
      }
    );
  }
);
HeroMedia.displayName = "HeroMedia";

// src/Hero/components/composed/HeroRoot.tsx
import { forwardRef as forwardRef9 } from "react";
import { Primitive as Primitive28 } from "@radix-ui/react-primitive";
import { cva as cva43 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/hero.min.css";
import { jsx as jsx53 } from "react/jsx-runtime";
var heroRoot = cva43("kui-hero-root", {
  variants: {
    align: {
      start: "kui-hero-root--align-start",
      center: "kui-hero-root--align-center",
      end: "kui-hero-root--align-end"
    }
  }
});
var HeroRoot = forwardRef9(
  ({ className, align = "start", ...props }, ref) => {
    return /* @__PURE__ */ jsx53(
      Primitive28.div,
      {
        ...mergeProps(
          {
            className: heroRoot({ className, align }),
            ref,
            "data-testid": HeroTestIds.HeroRoot
          },
          props
        )
      }
    );
  }
);
HeroRoot.displayName = "HeroRoot";

// src/Hero/components/composed/HeroSubHeading.tsx
import { forwardRef as forwardRef10 } from "react";
import { Primitive as Primitive29 } from "@radix-ui/react-primitive";
import { cva as cva44 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/hero.min.css";
import { jsx as jsx54 } from "react/jsx-runtime";
var heroSubHeading = cva44("kui-hero-sub-heading");
var HeroSubHeading = forwardRef10(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx54(
      Primitive29.div,
      {
        ...mergeProps(
          {
            className: heroSubHeading({ className }),
            ref,
            "data-testid": HeroTestIds.HeroSubHeading
          },
          props
        )
      }
    );
  }
);
HeroSubHeading.displayName = "HeroSubHeading";

// src/Hero/components/base/Hero.tsx
import { jsx as jsx55, jsxs as jsxs13 } from "react/jsx-runtime";
var Hero = ({
  align,
  slotMedia,
  slotHeading,
  slotSubHeading,
  slotBody,
  slotActions,
  ...props
}) => {
  return /* @__PURE__ */ jsxs13(HeroRoot, { align, ...props, children: [
    /* @__PURE__ */ jsx55(HeroMedia, { asChild: true, children: slotMedia }),
    /* @__PURE__ */ jsxs13(HeroContent, { children: [
      /* @__PURE__ */ jsxs13(HeroHeader, { children: [
        slotSubHeading && /* @__PURE__ */ jsx55(HeroSubHeading, { children: slotSubHeading }),
        /* @__PURE__ */ jsx55(HeroHeading, { children: slotHeading })
      ] }),
      /* @__PURE__ */ jsx55(HeroBody, { children: slotBody }),
      slotActions && /* @__PURE__ */ jsx55(HeroFooter, { children: slotActions })
    ] })
  ] });
};
Hero.displayName = "Hero";

// src/Inline/components/base/Inline.tsx
import React39 from "react";
import { cx as cx5 } from "class-variance-authority";

// src/Inline/constants.ts
var InlineTestIds = {
  Inline: "kui-inline"
};

// src/Inline/components/base/Inline.tsx
import { jsx as jsx56 } from "react/jsx-runtime";
var Inline = React39.forwardRef(
  ({ className, spacing, ...spanProps }, ref) => /* @__PURE__ */ jsx56(
    Primitive12.span,
    {
      ...mergeProps(
        {
          className: cx5(
            "kui-inline",
            primitive({
              className,
              spacing
            })
          ),
          ref,
          "data-testid": InlineTestIds.Inline
        },
        spanProps
      )
    }
  )
);
Inline.displayName = "Inline";

// src/ProgressBar/components/base/ProgressBar.tsx
import { forwardRef as forwardRef11 } from "react";
import * as RadixProgress from "@radix-ui/react-progress";
import { cva as cva45 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/progress-bar.min.css";

// src/ProgressBar/constants.ts
var ProgressBarTestIds = {
  Track: "kui-progress-bar-track",
  Indicator: "kui-progress-bar-indicator"
};

// src/ProgressBar/components/base/ProgressBar.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
var progressBar = cva45("kui-progress-bar", {
  variants: {
    size: {
      small: "kui-progress-bar--size-small",
      medium: "kui-progress-bar--size-medium",
      large: "kui-progress-bar--size-large"
    }
  },
  defaultVariants: {
    size: "medium"
  }
});
var ProgressBar = forwardRef11(
  ({ className, value, size, ...props }, ref) => {
    return /* @__PURE__ */ jsx57(
      RadixProgress.Root,
      {
        ...mergeProps(
          {
            className: progressBar({ className, size }),
            "data-testid": ProgressBarTestIds.Track,
            value,
            ref
          },
          props
        ),
        children: /* @__PURE__ */ jsx57(
          RadixProgress.Indicator,
          {
            className: "kui-progress-bar-indicator",
            "data-testid": ProgressBarTestIds.Indicator,
            style: {
              width: `${value}%`
            }
          }
        )
      }
    );
  }
);
ProgressBar.displayName = "ProgressBar";

// src/Skeleton/components/base/Skeleton.tsx
import { forwardRef as forwardRef12 } from "react";
import { Primitive as Primitive30 } from "@radix-ui/react-primitive";
import { cva as cva46 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/skeleton.min.css";

// src/Skeleton/constants.ts
var SkeletonTestIds = {
  Skeleton: "kui-skeleton"
};

// src/Skeleton/components/base/Skeleton.tsx
import { jsx as jsx58 } from "react/jsx-runtime";
var skeleton = cva46("kui-skeleton", {
  variants: {
    kind: {
      line: "kui-skeleton--kind-line",
      pill: "kui-skeleton--kind-pill",
      circle: "kui-skeleton--kind-circle"
    },
    animated: {
      true: "kui-skeleton--animated"
    }
  },
  defaultVariants: {
    kind: "line",
    animated: true
  }
});
var Skeleton = forwardRef12(
  ({ className, animated, kind, ...props }, ref) => {
    return /* @__PURE__ */ jsx58(
      Primitive30.div,
      {
        ...mergeProps(
          {
            className: skeleton({ className, kind, animated }),
            ref,
            "data-testid": SkeletonTestIds.Skeleton
          },
          props
        )
      }
    );
  }
);
Skeleton.displayName = "Skeleton";

// src/Spinner/components/base/Spinner.tsx
import { forwardRef as forwardRef13 } from "react";

// src/Spinner/constants.ts
var SpinnerTestIds = {
  Arrow: "kui-spinner-arrow",
  Spinner: "kui-spinner-spinner"
};

// src/Spinner/components/base/Spinner.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/spinner.min.css";
import React40 from "react";
import { cva as cva47 } from "class-variance-authority";
import { jsx as jsx59, jsxs as jsxs14 } from "react/jsx-runtime";
var spinnerStyles = cva47("kui-spinner", {
  variants: {
    size: {
      small: "kui-spinner--size-small",
      medium: "kui-spinner--size-medium",
      large: "kui-spinner--size-large"
    }
  },
  defaultVariants: {
    size: "medium"
  }
});
var Spinner = forwardRef13(
  ({ className, description, size, ...props }, ref) => {
    const id = React40.useId();
    return /* @__PURE__ */ jsxs14(
      "div",
      {
        role: "status",
        "aria-labelledby": description && id,
        className: spinnerStyles({ className, size }),
        "data-testid": SpinnerTestIds.Spinner,
        ref,
        ...props,
        children: [
          /* @__PURE__ */ jsx59("div", { children: /* @__PURE__ */ jsxs14("svg", { height: "100%", width: "100%", viewBox: "0 0 142 128", children: [
            /* @__PURE__ */ jsxs14("g", { transform: "translate(36, 0)", children: [
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-1s", direction: "up", y: "1" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.9444s", direction: "down", x: "18" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.8888s", direction: "up", x: "36", y: "1" })
            ] }),
            /* @__PURE__ */ jsxs14("g", { transform: "translate(18, 32)", children: [
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-1s", direction: "up", y: "1" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.9444s", direction: "down", x: "18" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.8888s", direction: "up", x: "36", y: "1" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.8333s", direction: "down", x: "54" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.7777s", direction: "up", x: "72", y: "1" })
            ] }),
            /* @__PURE__ */ jsxs14("g", { transform: "translate(0, 64)", children: [
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "0s", direction: "up", y: "1" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.1111s", direction: "down", x: "18" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-1s", direction: "up", x: "36", y: "1" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.8333s", direction: "down", x: "54" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.6666s", direction: "up", x: "72", y: "1" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.7222s", direction: "down", x: "90" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.6666s", direction: "up", x: "108", y: "1" })
            ] }),
            /* @__PURE__ */ jsxs14("g", { transform: "translate(0, 96)", children: [
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.1666s", direction: "down" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.2222s", direction: "up", x: "18", y: "1" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.1666s", direction: "down", x: "36" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.3333s", direction: "up", x: "54", y: "1" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.5s", direction: "down", x: "72" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.6111s", direction: "up", x: "90", y: "1" }),
              /* @__PURE__ */ jsx59(SpinnerArrow, { delay: "-.5555s", direction: "down", x: "108" })
            ] }),
            "Sorry, your browser does not support inline SVG."
          ] }) }),
          description && /* @__PURE__ */ jsx59("div", { className: "kui-spinner-description", id, children: description })
        ]
      }
    );
  }
);
var SpinnerArrow = ({
  delay,
  direction,
  y = "0",
  x = "0"
}) => {
  return /* @__PURE__ */ jsx59(
    "polygon",
    {
      className: "kui-spinner-arrow",
      "data-testid": SpinnerTestIds.Arrow,
      points: direction === "up" ? "17 0, 34 31, 0 31" : "0 0, 34 0, 17 31",
      transform: `translate(${x}, ${y})`,
      style: { animationDelay: delay }
    }
  );
};
Spinner.displayName = "Spinner";

// src/Stack/components/base/Stack.tsx
import React41 from "react";
import { jsx as jsx60 } from "react/jsx-runtime";
var Stack = React41.forwardRef(
  ({ direction = "col", ...props }, ref) => /* @__PURE__ */ jsx60(Flex, { ...props, direction, ref })
);
Stack.displayName = "Stack";

// src/StatusMessage/components/base/StatusMessage.tsx
import { forwardRef as forwardRef20 } from "react";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/status-message.min.css";

// src/StatusMessage/components/composed/StatusMessageBody.tsx
import { forwardRef as forwardRef14 } from "react";
import { Primitive as Primitive31 } from "@radix-ui/react-primitive";
import { cva as cva48 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/status-message.min.css";

// src/StatusMessage/constants.ts
var StatusMessageTestIds = {
  StatusMessageRoot: "kui-status-message-root",
  StatusMessageMedia: "kui-status-message-media",
  StatusMessageContent: "kui-status-message-content",
  StatusMessageHeader: "kui-status-message-header",
  StatusMessageBody: "kui-status-message-body",
  StatusMessageFooter: "kui-status-message-footer"
};

// src/StatusMessage/components/composed/StatusMessageBody.tsx
import { jsx as jsx61 } from "react/jsx-runtime";
var statusMessageBody = cva48("kui-status-message-body");
var StatusMessageBody = forwardRef14(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx61(
    Primitive31.div,
    {
      ...mergeProps(
        {
          className: statusMessageBody({ className }),
          ref,
          "data-testid": StatusMessageTestIds.StatusMessageBody
        },
        props
      )
    }
  );
});
StatusMessageBody.displayName = "StatusMessageBody";

// src/StatusMessage/components/composed/StatusMessageContent.tsx
import { forwardRef as forwardRef15 } from "react";
import { Primitive as Primitive32 } from "@radix-ui/react-primitive";
import { cva as cva49 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/status-message.min.css";
import { jsx as jsx62 } from "react/jsx-runtime";
var statusMessageContent = cva49("kui-status-message-content");
var StatusMessageContent = forwardRef15(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx62(
    Primitive32.div,
    {
      ...mergeProps(
        {
          className: statusMessageContent({ className }),
          ref,
          "data-testid": StatusMessageTestIds.StatusMessageContent
        },
        props
      )
    }
  );
});
StatusMessageContent.displayName = "StatusMessageContent";

// src/StatusMessage/components/composed/StatusMessageFooter.tsx
import { forwardRef as forwardRef16 } from "react";
import { Primitive as Primitive33 } from "@radix-ui/react-primitive";
import { cva as cva50 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/status-message.min.css";
import { jsx as jsx63 } from "react/jsx-runtime";
var statusMessageFooter = cva50("kui-status-message-footer");
var StatusMessageFooter = forwardRef16(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx63(
    Primitive33.div,
    {
      ...mergeProps(
        {
          className: statusMessageFooter({ className }),
          ref,
          "data-testid": StatusMessageTestIds.StatusMessageFooter
        },
        props
      )
    }
  );
});
StatusMessageFooter.displayName = "StatusMessageFooter";

// src/StatusMessage/components/composed/StatusMessageHeader.tsx
import { forwardRef as forwardRef17 } from "react";
import { Primitive as Primitive34 } from "@radix-ui/react-primitive";
import { cva as cva51 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/status-message.min.css";
import { jsx as jsx64 } from "react/jsx-runtime";
var statusMessageHeader = cva51("kui-status-message-header");
var StatusMessageHeader = forwardRef17(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx64(
    Primitive34.div,
    {
      ...mergeProps(
        {
          className: statusMessageHeader({ className }),
          ref,
          "data-testid": StatusMessageTestIds.StatusMessageHeader
        },
        props
      )
    }
  );
});
StatusMessageHeader.displayName = "StatusMessageHeader";

// src/StatusMessage/components/composed/StatusMessageMedia.tsx
import { forwardRef as forwardRef18 } from "react";
import { Primitive as Primitive35 } from "@radix-ui/react-primitive";
import { cva as cva52 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/status-message.min.css";
import { jsx as jsx65 } from "react/jsx-runtime";
var statusMessageMedia = cva52("kui-status-message-media");
var StatusMessageMedia = forwardRef18(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx65(
    Primitive35.div,
    {
      ...mergeProps(
        {
          className: statusMessageMedia({ className }),
          ref,
          "data-testid": StatusMessageTestIds.StatusMessageMedia
        },
        props
      )
    }
  );
});
StatusMessageMedia.displayName = "StatusMessageMedia";

// src/StatusMessage/components/composed/StatusMessageRoot.tsx
import { forwardRef as forwardRef19 } from "react";
import { Primitive as Primitive36 } from "@radix-ui/react-primitive";
import { cva as cva53 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/status-message.min.css";
import { jsx as jsx66 } from "react/jsx-runtime";
var statusMessageRoot = cva53("kui-status-message-root", {
  variants: {
    size: {
      small: "kui-status-message-root--small",
      medium: "kui-status-message-root--medium"
    }
  },
  defaultVariants: {
    size: "medium"
  }
});
var StatusMessageRoot = forwardRef19(({ className, size, ...props }, ref) => {
  return /* @__PURE__ */ jsx66(
    Primitive36.div,
    {
      ...mergeProps(
        {
          className: statusMessageRoot({ className, size }),
          ref,
          "data-testid": StatusMessageTestIds.StatusMessageRoot
        },
        props
      )
    }
  );
});
StatusMessageRoot.displayName = "StatusMessageRoot";

// src/StatusMessage/components/base/StatusMessage.tsx
import { jsx as jsx67, jsxs as jsxs15 } from "react/jsx-runtime";
var StatusMessage = forwardRef20(
  ({ className, size, slotMedia, slotHeader, slotBody, slotFooter, ...props }, ref) => {
    return /* @__PURE__ */ jsxs15(StatusMessageRoot, { ...mergeProps({ size, ref, className }, props), children: [
      /* @__PURE__ */ jsx67(StatusMessageMedia, { children: slotMedia }),
      /* @__PURE__ */ jsxs15(StatusMessageContent, { children: [
        /* @__PURE__ */ jsx67(StatusMessageHeader, { children: slotHeader }),
        /* @__PURE__ */ jsx67(StatusMessageBody, { children: slotBody })
      ] }),
      /* @__PURE__ */ jsx67(StatusMessageFooter, { children: slotFooter })
    ] });
  }
);
StatusMessage.displayName = "StatusMessage";

// src/Switch/components/base/Switch.tsx
import { forwardRef as forwardRef24, useId } from "react";

// src/Switch/components/composed/SwitchLabel.tsx
import { cva as cva54 } from "class-variance-authority";

// src/Switch/constants.ts
var SwitchTestIds = {
  Root: "kui-switch-root",
  Track: "kui-switch-track",
  Thumb: "kui-switch-thumb",
  Label: "kui-switch-label"
};

// src/Switch/components/composed/SwitchLabel.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/switch.min.css";
import { jsx as jsx68 } from "react/jsx-runtime";
var switchLabel = cva54("", {
  variants: {
    size: {
      small: "",
      medium: "",
      large: "kui-switch-label--size-large"
    }
  },
  defaultVariants: {
    size: "medium"
  }
});
var SwitchLabel = ({
  size = "medium",
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx68(
    Label,
    {
      ...mergeProps(
        {
          className: switchLabel({ className, size }),
          "data-testid": SwitchTestIds.Label
        },
        props
      )
    }
  );
};
SwitchLabel.displayName = "SwitchLabel";

// src/Switch/components/composed/SwitchRoot.tsx
import { forwardRef as forwardRef21 } from "react";
import { Primitive as Primitive37 } from "@radix-ui/react-primitive";
import { cva as cva55 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/switch.min.css";
import { jsx as jsx69 } from "react/jsx-runtime";
var switchRoot = cva55("kui-switch-root", {
  variants: {
    labelSide: {
      left: "kui-switch-root--labelSide-left",
      right: ""
    }
  },
  defaultVariants: {
    labelSide: "right"
  }
});
var SwitchRoot = forwardRef21(
  ({ labelSide = "right", className, ...props }, ref) => {
    return /* @__PURE__ */ jsx69(
      Primitive37.div,
      {
        ...mergeProps(
          {
            className: switchRoot({ className, labelSide }),
            ref,
            "data-testid": SwitchTestIds.Root
          },
          props
        )
      }
    );
  }
);
SwitchRoot.displayName = "SwitchRoot";

// src/Switch/components/composed/SwitchThumb.tsx
import { forwardRef as forwardRef22 } from "react";
import * as RadixSwitch from "@radix-ui/react-switch";
import { cva as cva56 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/switch.min.css";
import { jsx as jsx70 } from "react/jsx-runtime";
var switchThumb = cva56("kui-switch-thumb", {
  variants: {
    size: {
      small: "kui-switch-thumb--size-small",
      medium: "kui-switch-thumb--size-medium",
      large: "kui-switch-thumb--size-large"
    }
  },
  defaultVariants: {
    size: "medium"
  }
});
var SwitchThumb = forwardRef22(
  ({ size = "medium", className, ...props }, ref) => {
    return /* @__PURE__ */ jsx70(
      RadixSwitch.Thumb,
      {
        ...mergeProps(
          {
            className: switchThumb({ className, size }),
            ref,
            "data-testid": SwitchTestIds.Thumb
          },
          props
        )
      }
    );
  }
);
SwitchThumb.displayName = "SwitchThumb";

// src/Switch/components/composed/SwitchTrack.tsx
import { forwardRef as forwardRef23 } from "react";
import * as RadixSwitch2 from "@radix-ui/react-switch";
import { cva as cva57 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/switch.min.css";
import { jsx as jsx71 } from "react/jsx-runtime";
var switchTrack = cva57("kui-switch-track", {
  variants: {
    size: {
      small: "kui-switch-track--size-small",
      medium: "kui-switch-track--size-medium",
      large: "kui-switch-track--size-large"
    }
  },
  defaultVariants: {
    size: "medium"
  }
});
var SwitchTrack = forwardRef23(
  ({ size = "medium", className, ...props }, ref) => {
    return /* @__PURE__ */ jsx71(
      RadixSwitch2.Root,
      {
        ...mergeProps(
          {
            className: switchTrack({ className, size }),
            ref,
            "data-testid": SwitchTestIds.Track
          },
          props
        )
      }
    );
  }
);
SwitchTrack.displayName = "SwitchTrack";

// src/Switch/components/base/Switch.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/switch.min.css";
import { jsx as jsx72, jsxs as jsxs16 } from "react/jsx-runtime";
var Switch = forwardRef24(
  ({
    labelSide = "right",
    size = "medium",
    checked,
    defaultChecked,
    label: label2,
    disabled,
    className,
    ...props
  }, ref) => {
    const labelId = useId();
    const trackId = useId();
    return /* @__PURE__ */ jsxs16(SwitchRoot, { labelSide, children: [
      /* @__PURE__ */ jsx72(
        SwitchTrack,
        {
          "aria-labelledby": labelId,
          ...mergeProps(
            {
              className,
              size,
              disabled,
              checked: checked ?? defaultChecked,
              id: trackId,
              ref
            },
            props
          ),
          children: /* @__PURE__ */ jsx72(SwitchThumb, { size })
        }
      ),
      label2 && /* @__PURE__ */ jsx72(
        SwitchLabel,
        {
          id: labelId,
          htmlFor: trackId,
          size,
          disabled,
          children: label2
        }
      )
    ] });
  }
);
Switch.displayName = "Switch";

// src/TextInput/components/base/TextInput.tsx
import { forwardRef as forwardRef27 } from "react";

// src/TextInput/components/composed/TextInputRoot.tsx
import { forwardRef as forwardRef25 } from "react";
import { Primitive as Primitive38 } from "@radix-ui/react-primitive";
import { cva as cva58 } from "class-variance-authority";
import {
  Check as CheckIcon,
  Error as ErrorIcon
} from "@nv-brand-assets/react-icons-inline";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/text-input.min.css";

// src/TextInput/constants.ts
var TextInputTestIds = {
  TextInputRoot: "kui-text-input-root",
  TextInputValue: "kui-text-input-value"
};

// src/TextInput/components/composed/TextInputRoot.tsx
import { jsx as jsx73, jsxs as jsxs17 } from "react/jsx-runtime";
var textInputRoot = cva58("kui-text-input", {
  variants: {
    size: {
      small: "kui-text-input--size-sm",
      medium: "kui-text-input--size-md",
      large: "kui-text-input--size-lg"
    },
    status: {
      error: "kui-text-input--status-error",
      success: "kui-text-input--status-success"
    }
  },
  defaultVariants: {
    size: "medium"
  }
});
var TextInputRoot = forwardRef25(
  ({ children, className, size, status, ...props }, ref) => {
    return /* @__PURE__ */ jsxs17(
      Primitive38.div,
      {
        ...mergeProps(
          {
            className: textInputRoot({ className, size, status }),
            ref,
            "data-testid": TextInputTestIds.TextInputRoot
          },
          props
        ),
        children: [
          children,
          status === "success" && /* @__PURE__ */ jsx73(CheckIcon, { variant: "fill", className: "kui-text-input-status-icon" }),
          status === "error" && /* @__PURE__ */ jsx73(ErrorIcon, { variant: "fill", className: "kui-text-input-status-icon" })
        ]
      }
    );
  }
);
TextInputRoot.displayName = "TextInputRoot";

// src/TextInput/components/composed/TextInputValue.tsx
import { forwardRef as forwardRef26, useCallback } from "react";
import { Primitive as Primitive39 } from "@radix-ui/react-primitive";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/text-input.min.css";
import { jsx as jsx74 } from "react/jsx-runtime";
var TextInputValue = forwardRef26(
  ({ className, onChange, onValueChange, ...props }, ref) => {
    const handleChange = useCallback(
      (e) => {
        onChange?.(e);
        if (!e.defaultPrevented) {
          onValueChange?.(e.target.value);
        }
      },
      [onChange, onValueChange]
    );
    return /* @__PURE__ */ jsx74(
      Primitive39.input,
      {
        type: "text",
        ...mergeProps(
          {
            className,
            ref,
            "data-testid": TextInputTestIds.TextInputValue
          },
          props
        ),
        onChange: handleChange
      }
    );
  }
);
TextInputValue.displayName = "TextInputValue";

// src/TextInput/components/base/TextInput.tsx
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/text-input.min.css";
import { jsx as jsx75, jsxs as jsxs18 } from "react/jsx-runtime";
var TextInput = forwardRef27(
  ({ className, size, slotLeft, slotRight, status, ...props }, ref) => {
    return /* @__PURE__ */ jsxs18(TextInputRoot, { className, size, status, children: [
      slotLeft,
      /* @__PURE__ */ jsx75(
        TextInputValue,
        {
          ...mergeProps(
            {
              ref
            },
            props
          )
        }
      ),
      slotRight
    ] });
  }
);
TextInput.displayName = "TextInput";

// src/Tooltip/components/base/Tooltip.tsx
import React43, { useContext } from "react";

// src/Tooltip/components/composed/TooltipContent.tsx
import { forwardRef as forwardRef28 } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { cva as cva59 } from "class-variance-authority";
import "@kui/foundations/dist/base.min.css";
import "@kui/foundations/dist/tooltip.min.css";

// src/Tooltip/constants.ts
var TooltipTestIds = {
  Root: "kui-tooltip-root",
  Trigger: "kui-tooltip-trigger",
  Content: "kui-tooltip-content"
};

// src/Tooltip/components/composed/TooltipContent.tsx
import { Fragment as Fragment3, jsx as jsx76, jsxs as jsxs19 } from "react/jsx-runtime";
var tooltipContent = cva59("kui-tooltip-content");
var TooltipContent = forwardRef28(
  ({
    className,
    message,
    content,
    position = "top",
    positionOffset = 0,
    alignment = "center",
    showArrow = true,
    ...props
  }, ref) => {
    const mergedContent = content || message;
    return /* @__PURE__ */ jsx76(
      Tooltip.Content,
      {
        ...mergeProps(
          {
            className: tooltipContent({ className }),
            side: position,
            sideOffset: positionOffset,
            align: alignment,
            "data-testid": TooltipTestIds.Content,
            ref
          },
          props
        ),
        children: /* @__PURE__ */ jsxs19(Fragment3, { children: [
          mergedContent,
          showArrow && /* @__PURE__ */ jsx76(Tooltip.Arrow, { asChild: true, children: /* @__PURE__ */ jsx76(Arrow2, {}) })
        ] })
      }
    );
  }
);
var Arrow2 = () => {
  return /* @__PURE__ */ jsxs19("div", { className: "kui-tooltip-arrow", children: [
    /* @__PURE__ */ jsx76(
      "svg",
      {
        width: "12",
        height: "8",
        viewBox: "0 0 12 8",
        xmlns: "http://www.w3.org/2000/svg",
        children: /* @__PURE__ */ jsx76("path", { d: "M7.66376 6.49999C6.87204 7.68677 5.12796 7.68677 4.33624 6.49999L0 0L6 4.91423e-07L12 9.82845e-07L7.66376 6.49999Z" })
      }
    ),
    /* @__PURE__ */ jsx76(
      "svg",
      {
        width: "10",
        height: "7",
        viewBox: "0 0 10 7",
        xmlns: "http://www.w3.org/2000/svg",
        children: /* @__PURE__ */ jsx76("path", { d: "M6.696 5.28641C5.91266 6.53974 4.08734 6.53974 3.304 5.28641L0 0L5 4.37114e-07L10 8.74228e-07L6.696 5.28641Z" })
      }
    )
  ] });
};
TooltipContent.displayName = "TooltipContent";

// src/Tooltip/components/composed/TooltipPortal.tsx
import { forwardRef as forwardRef29 } from "react";
import * as Tooltip2 from "@radix-ui/react-tooltip";
import { jsx as jsx77 } from "react/jsx-runtime";
var TooltipPortal = forwardRef29(
  ({ children, ...props }, ref) => {
    return /* @__PURE__ */ jsx77(
      Tooltip2.Portal,
      {
        ...mergeProps(
          {
            ref
          },
          props
        ),
        children
      }
    );
  }
);
TooltipPortal.displayName = "TooltipPortal";

// src/Tooltip/components/composed/TooltipProvider.tsx
import { createContext, forwardRef as forwardRef30 } from "react";
import * as Tooltip3 from "@radix-ui/react-tooltip";
import { jsx as jsx78 } from "react/jsx-runtime";
var TooltipContext = createContext(false);
var TooltipProvider = forwardRef30(
  ({ openDelayDuration = 500, skipDelayDuration = 300, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx78(TooltipContext.Provider, { value: true, children: /* @__PURE__ */ jsx78(
      Tooltip3.Provider,
      {
        ...mergeProps(
          {
            delayDuration: openDelayDuration,
            skipDelayDuration,
            ref
          },
          props
        ),
        children
      }
    ) });
  }
);
TooltipProvider.displayName = "TooltipProvider";

// src/Tooltip/components/composed/TooltipRoot.tsx
import { forwardRef as forwardRef31 } from "react";
import * as Tooltip4 from "@radix-ui/react-tooltip";
import { cva as cva60 } from "class-variance-authority";
import { jsx as jsx79 } from "react/jsx-runtime";
var tooltipRoot = cva60("kui-tooltip-root");
var TooltipRoot = forwardRef31(
  ({ className, openDelayDuration, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx79(
      Tooltip4.Root,
      {
        ...mergeProps(
          {
            className: tooltipRoot({ className }),
            delayDuration: openDelayDuration,
            "data-testid": TooltipTestIds.Root,
            ref
          },
          props
        ),
        children
      }
    );
  }
);
TooltipRoot.displayName = "TooltipRoot";

// src/Tooltip/components/composed/TooltipTrigger.tsx
import { forwardRef as forwardRef32 } from "react";
import * as Tooltip5 from "@radix-ui/react-tooltip";
import { jsx as jsx80 } from "react/jsx-runtime";
var TooltipTrigger = forwardRef32(({ asChild = false, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx80(
    Tooltip5.Trigger,
    {
      ...mergeProps(
        {
          asChild,
          "data-testid": TooltipTestIds.Trigger,
          ref
        },
        props
      ),
      children
    }
  );
});
TooltipTrigger.displayName = "TooltipTrigger";

// src/Tooltip/components/base/Tooltip.tsx
import { jsx as jsx81, jsxs as jsxs20 } from "react/jsx-runtime";
var Tooltip6 = React43.forwardRef(
  ({
    message,
    content,
    children,
    className,
    onOpenChange,
    open,
    container,
    position = "top",
    positionOffset = 0,
    alignment = "center",
    openDelayDuration,
    showArrow = true,
    asChild = false,
    ...props
  }, ref) => {
    const isInTooltipProvider = useContext(TooltipContext);
    const Component = /* @__PURE__ */ jsxs20(TooltipRoot, { open, onOpenChange, children: [
      /* @__PURE__ */ jsx81(TooltipTrigger, { asChild, children }),
      /* @__PURE__ */ jsx81(TooltipPortal, { container, children: /* @__PURE__ */ jsx81(
        TooltipContent,
        {
          content: content || message,
          ...mergeProps(
            {
              className,
              ref,
              alignment,
              position,
              positionOffset,
              showArrow
            },
            props
          )
        }
      ) })
    ] });
    return isInTooltipProvider ? Component : /* @__PURE__ */ jsx81(TooltipProvider, { openDelayDuration, children: Component });
  }
);
Tooltip6.displayName = "Tooltip";
export {
  Accordion,
  AccordionContent,
  AccordionIcon,
  AccordionItem2 as AccordionItem,
  AccordionRoot,
  AccordionTestIds,
  AccordionTrigger,
  AppBar,
  AppBarTestIds,
  Avatar2 as Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarRoot,
  AvatarTestIds,
  Badge,
  Banner,
  BannerAction,
  BannerCloseButton,
  BannerContent,
  BannerDescription,
  BannerIcon,
  BannerRoot,
  BannerTestIds,
  BannerTextGroup,
  BannerTitle,
  Block,
  BlockTestIds,
  BreadcrumbCurrentItem,
  BreadcrumbItem,
  BreadcrumbSeparator,
  Breadcrumbs,
  BreadcrumbsRoot,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardRoot,
  CardTestIds,
  Checkbox,
  CheckboxBox,
  CheckboxIndicator,
  CheckboxLabel,
  CheckboxRoot,
  CheckboxTestIds,
  Flex,
  Grid,
  GridItem,
  Hero,
  HeroBody,
  HeroContent,
  HeroFooter,
  HeroHeader,
  HeroHeading,
  HeroMedia,
  HeroRoot,
  HeroSubHeading,
  HeroTestIds,
  Inline,
  Label,
  LabelTestId,
  Logo,
  ProgressBar,
  ProgressBarTestIds,
  Skeleton,
  SkeletonTestIds,
  Spinner,
  Stack,
  StatusMessage,
  StatusMessageBody,
  StatusMessageContent,
  StatusMessageFooter,
  StatusMessageHeader,
  StatusMessageMedia,
  StatusMessageRoot,
  StatusMessageTestIds,
  Switch,
  SwitchLabel,
  SwitchRoot,
  SwitchTestIds,
  SwitchThumb,
  SwitchTrack,
  TextInput,
  TextInputRoot,
  TextInputTestIds,
  TextInputValue,
  Tooltip6 as Tooltip,
  TooltipContent,
  TooltipContext,
  TooltipPortal,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  badgeTestId,
  gridItemTestId,
  logoMap
};
